import { useMemo } from 'react';
import type { Device } from '@types';
import type { HeartRateEvent } from '@types';
import {
  createLookupMaps,
  processSessionData,
  extractUniqueTypes,
  type EnrichedRecord,
} from '../utils/dataTransformations';
import { extractAvailableStatuses } from '../utils/filters';

import type { ConfigurationRecord as BaseConfigurationRecord } from '../utils/dataTransformations';

type ConfigurationRecord = BaseConfigurationRecord;

type UseSessionDataParams = {
  configData: ConfigurationRecord[];
  devices: Device[];
  events: HeartRateEvent[];
  videosData: Array<{ id: number | string; name: string }>;
};

type UseSessionDataReturn = {
  processedData: EnrichedRecord[];
  uniqueTypes: string[];
  availableStatuses: string[];
};

export const useSessionData = ({
  configData,
  devices,
  events,
  videosData,
}: UseSessionDataParams): UseSessionDataReturn => {
  const lookups = useMemo(
    () => createLookupMaps(devices, events, videosData),
    [devices, events, videosData]
  );

  const processedData = useMemo(
    () => processSessionData(configData, lookups),
    [configData, lookups]
  );

  const uniqueTypes = useMemo(
    () => extractUniqueTypes(processedData),
    [processedData]
  );

  const availableStatuses = useMemo(
    () => extractAvailableStatuses(processedData),
    [processedData]
  );

  return {
    processedData,
    uniqueTypes,
    availableStatuses,
  };
};
